import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../utils/reduxHelper";

const ENGAGE_COMMENTS_BASE = "c/engage/comments";
const SAVED_REPLIES_BASE = "c/engage/me/saved-replies";

const buildCommentsQuery = ({
  filter = "all",
  sort = "date_desc",
  postId,
  cursor,
  limit = 20,
}) => {
  const params = new URLSearchParams();
  params.set("filter", filter);
  params.set("sort", sort);
  params.set("limit", String(limit));
  if (postId) params.set("post_id", String(postId));
  if (cursor) params.set("cursor", cursor);
  return getQueryStringWithCreator(
    `${ENGAGE_COMMENTS_BASE}?${params.toString()}`
  );
};

export const engageCommentsReduxAPI = createApi({
  reducerPath: "engageCommentsReduxAPI",
  baseQuery: baseQueryWithArgs,
  tagTypes: ["EngageComments", "EngageCommentsByPost", "SavedReplies"],
  endpoints: (builder) => ({
    getEngageComments: builder.query({
      query: (args = {}) => ({
        url: buildCommentsQuery(args),
        method: "GET",
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        }
        return response?.data ?? response;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { filter = "all", sort = "date_desc", postId } = queryArgs || {};
        return `${endpointName}?filter=${filter}&sort=${sort}&postId=${
          postId || ""
        }`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg?.cursor) {
          return newItems;
        }
        const existingItems = currentCache?.items || [];
        const newPageItems = newItems?.items || [];
        return {
          ...newItems,
          items: [...existingItems, ...newPageItems],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.cursor !== previousArg?.cursor ||
        currentArg?.filter !== previousArg?.filter ||
        currentArg?.sort !== previousArg?.sort ||
        currentArg?.postId !== previousArg?.postId,
      providesTags: ["EngageComments"],
    }),
    getEngageCommentsByPost: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`${ENGAGE_COMMENTS_BASE}/by-post`),
        method: "GET",
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        }
        return response?.data ?? response;
      },
      providesTags: ["EngageCommentsByPost"],
    }),
    getEngageCommentsUnreadCount: builder.query({
      query: () => ({
        url: buildCommentsQuery({ filter: "unreplied", limit: 1 }),
        method: "GET",
      }),
      transformResponse: (response) => {
        const data = response?.success
          ? response.data
          : response?.data ?? response;
        return {
          count: data?.totalCount ?? data?.items?.length ?? 0,
        };
      },
      providesTags: ["EngageComments"],
    }),
    getEngageCommentsFilterCounts: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`${ENGAGE_COMMENTS_BASE}/counts`),
        method: "GET",
      }),
      transformResponse: (response) => {
        const data = response?.success
          ? response.data
          : response?.data ?? response;
        return data?.counts || {};
      },
      providesTags: ["EngageComments"],
    }),
    sendCommentReply: builder.mutation({
      query: ({ commentId, text, savedReplyId }) => ({
        url: getQueryStringWithCreator(
          `${ENGAGE_COMMENTS_BASE}/${commentId}/reply`
        ),
        method: "POST",
        body: postQueryBodyWithCreator({
          text,
          ...(savedReplyId ? { saved_reply_id: savedReplyId } : {}),
        }),
      }),
      transformResponse: (response) =>
        response?.success ? response.data : response?.data ?? response,
      invalidatesTags: [
        "EngageComments",
        "EngageCommentsByPost",
        "SavedReplies",
      ],
    }),
    dismissEngageComment: builder.mutation({
      query: ({ commentId }) => ({
        url: getQueryStringWithCreator(
          `${ENGAGE_COMMENTS_BASE}/${commentId}/dismiss`
        ),
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      invalidatesTags: ["EngageComments", "EngageCommentsByPost"],
    }),
    getSavedReplies: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(SAVED_REPLIES_BASE),
        method: "GET",
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        }
        return response?.data ?? response;
      },
      providesTags: ["SavedReplies"],
    }),
    createSavedReply: builder.mutation({
      query: ({ text }) => ({
        url: getQueryStringWithCreator(SAVED_REPLIES_BASE),
        method: "POST",
        body: postQueryBodyWithCreator({ text }),
      }),
      transformResponse: (response) =>
        response?.success ? response.data : response?.data ?? response,
      invalidatesTags: ["SavedReplies"],
    }),
    updateSavedReply: builder.mutation({
      query: ({ id, text }) => ({
        url: getQueryStringWithCreator(`${SAVED_REPLIES_BASE}/${id}`),
        method: "PATCH",
        body: postQueryBodyWithCreator({ text }),
      }),
      transformResponse: (response) =>
        response?.success ? response.data : response?.data ?? response,
      invalidatesTags: ["SavedReplies"],
    }),
    deleteSavedReply: builder.mutation({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`${SAVED_REPLIES_BASE}/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: ["SavedReplies"],
    }),
  }),
});

export const {
  useGetEngageCommentsQuery,
  useLazyGetEngageCommentsQuery,
  useGetEngageCommentsByPostQuery,
  useGetEngageCommentsUnreadCountQuery,
  useGetEngageCommentsFilterCountsQuery,
  useSendCommentReplyMutation,
  useDismissEngageCommentMutation,
  useGetSavedRepliesQuery,
  useCreateSavedReplyMutation,
  useUpdateSavedReplyMutation,
  useDeleteSavedReplyMutation,
} = engageCommentsReduxAPI;

export default engageCommentsReduxAPI;
